import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  Icon,
  Text,
} from '@flatfile/baseline'
import { SetStateAction, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { deleteMappingSession } from '../../api/overrides/mapping'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceHeaderComponent, ResourceRowComponent } from '../../components/ResourceList/types'
import useResource from '../../resources/hooks/useResource'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'

export const CustomAppTableHeader: ResourceHeaderComponent = () => {
  return (
    <thead className='border-b border-gray-200'>
      <tr>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Program Id
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Accuracy
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Changes Made
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Source
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Destination
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Created By
          </Text>
        </th>
        <th colSpan={2} className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted'>
            Created At
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'></th>
      </tr>
    </thead>
  )
}

const customRowGenerator = ({
  setRuleContent,
  setProgramId,
}: {
  setRuleContent: { (value: SetStateAction<string>): void; (arg0: string): void }
  setProgramId: { (value: SetStateAction<string>): void; (arg0: string): void }
}) => {
  const CustomAppRow: ResourceRowComponent = ({ resourceId, selected, onSelect }) => {
    const program = useResource<any>('MAPPING_PROGRAMS', resourceId)
    if (!program) {
      return <></>
    }
    return (
      <tr className='hover:bg-gray-50'>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <span className='text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded text-gray-700'>{program.id}</span>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <Text variant='body' color='primary' className='text-sm'>
            {Math.round(
              (100 *
                (program.summary.totalRuleCount - program.summary.addedRuleCount - program.summary.deletedRuleCount)) /
                program.summary.totalRuleCount
            )}
            %
          </Text>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <Text variant='body' color='primary' className='text-sm'>
            {program.summary.addedRuleCount + program.summary.deletedRuleCount}
          </Text>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <span className='text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded text-gray-700'>
            {program.sourceKeys.join(', ')}
          </span>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <span className='text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded text-gray-700'>
            {program.destinationKeys.join(', ')}
          </span>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <Text variant='body' color='primary' className='text-sm'>
            {program.createdBy}
          </Text>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <Text variant='body' color='primary' className='text-sm'>
            {new Date(program.createdAt).toLocaleDateString()}
          </Text>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <Text variant='body' color='primary' className='text-sm'>
            {new Date(program.createdAt).toLocaleTimeString()}
          </Text>
        </td>
        <td className='p-2 text-left align-middle border-b border-gray-200'>
          <Button
            size='sm'
            onClick={() => {
              setProgramId(program.id)
              setRuleContent(
                !selected
                  ? JSON.stringify(
                      program.rules.map((r: any) => ({
                        ...(r.deletedAt ? { deletedAt: r.deletedAt } : {}),
                        ...r.config,
                      })),
                      null,
                      2
                    )
                  : ''
              )
              return onSelect && onSelect(program ? program.id : '')
            }}
          >
            Inspect
          </Button>
        </td>
      </tr>
    )
  }
  return CustomAppRow
}

export const MappingListDetailPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const pagination: ListPagination = { currentPage, setCurrentPage, pageCount, setPageCount }
  const [rules, setRuleContent] = useState<string>('')
  const [programId, setProgramId] = useState<string>('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { id = '' } = useParams()

  const { addToRefsArray, listHeight } = useDynamicHeight()

  const handleDeleteSession = () => {
    deleteMappingSession({ sessionId: programId }).then(() => {
      window.location.reload()
    })
  }

  const handleCopyRules = () => {
    navigator.clipboard.writeText(rules)
  }

  return (
    <>
      <div className='px-4 py-3 min-h-[62px]' ref={addToRefsArray}>
        <div className='flex items-center gap-2'>
          <NavLink to='/apps/mapping/list/recent/' relative='path'>
            <Text variant='title3' emphasized>
              Mapping History
            </Text>
          </NavLink>
          <Icon name='chevron-right' size={12} />
          <Text variant='body' className='text-sm'>
            {id}
          </Text>
        </div>
      </div>

      <ResourceListContextProvider requestParams={{ familyId: id }} resourceType='MAPPING_PROGRAMS'>
        <ResourceList
          height={listHeight}
          errorMessage='Unable to load programs'
          emptyMessage='No programs to display.'
          headerComponent={CustomAppTableHeader}
          rowComponent={customRowGenerator({ setRuleContent, setProgramId })}
          pagination={pagination}
        />
      </ResourceListContextProvider>

      <Drawer open={rules.length > 0} onOpenChange={() => setRuleContent('')}>
        <DrawerContent className='p-6'>
          <div className='flex justify-between mb-4'>
            <Text variant='title3' emphasized>
              Rules
            </Text>
            <div className='flex gap-2'>
              <Button variant='outline' onClick={handleCopyRules} className='h-9'>
                Copy
              </Button>
              <Button variant='destructive' onClick={() => setDeleteDialogOpen(true)} className='h-9'>
                Remove From History
              </Button>
            </div>
          </div>
          <pre className='mt-4 p-4 bg-gray-50 rounded-md whitespace-pre-wrap overflow-auto max-h-[80vh] text-sm'>
            {rules}
          </pre>
        </DrawerContent>
      </Drawer>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Please confirm deletion</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Deleting this program will remove it from your mapping history. It will no longer influence mappings on this
            environment.
          </DialogDescription>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleDeleteSession}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default MappingListDetailPage
